import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getFullImageUrl } from '../services/api';
import './ProfileCard.css';

// 本地内联 SVG 占位：蓝白风姓名首字母，不依赖外网图床
const buildFallbackAvatar = (name = 'GJ') => {
  const initials = String(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('') || 'GJ';
  return (
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200">
        <defs>
          <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stop-color="#3b82f6"/>
            <stop offset="100%" stop-color="#1d4ed8"/>
          </linearGradient>
        </defs>
        <rect width="200" height="200" rx="28" fill="url(#g)"/>
        <text x="100" y="118" text-anchor="middle" font-family="Segoe UI, Arial, sans-serif" font-size="64" font-weight="700" fill="#ffffff">${initials}</text>
      </svg>`
    )
  );
};

const FALLBACK_AVATAR = buildFallbackAvatar('Gary Joe');

const ProfileCard = () => {
  const navigate = useNavigate();
  // 不预填默认头�?URL，避免刷新瞬间闪现旧�?
  const [profileData, setProfileData] = useState({
    name: '',
    nameCN: '',
    title: '',
    titleCN: '',
    avatar: '',
    verified: false,
    followLink: '/register',
    followButtonText: 'Follow',
    stats: {
      experience: 0,
      trades: 0,
      winRate: 0,
    },
  });
  const [loading, setLoading] = useState(true);
  const [avatarLoaded, setAvatarLoaded] = useState(false);

  // 真正用于 <img> �?URL；接口拿到后再计算，否则返回 ''
  const getAvatarUrl = (avatar) => {
    if (!avatar) return '';
    return getFullImageUrl(avatar, FALLBACK_AVATAR);
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/profile');
      if (response.data.success) {
        setAvatarLoaded(false); // 数据更新后等图重�?onLoad
        setProfileData(response.data.data.profile);
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFollowClick = () => {
    const link = profileData.followLink;
    if (link) {
      if (link.startsWith('http')) {
        window.open(link, '_blank');
      } else {
        navigate(link);
      }
    }
  };

  return (
    <div className={`profile-card ${loading ? 'profile-card--loading' : ''}`}>
      <div className="profile-card__content">
        {/* Avatar Section */}
        <div className="profile-card__avatar-section">
          <div className="profile-card__avatar-wrapper">
            <div className="profile-card__avatar-glow"></div>
            {/* 加载中显示骨架占位；图片 onLoad 完成后再淡入 */}
            {(!avatarLoaded || loading) && (
              <div className="profile-card__avatar-skeleton" aria-hidden="true" />
            )}
            {!loading && (
              <img
                src={getAvatarUrl(profileData.avatar) || buildFallbackAvatar(profileData.name || 'Gary Joe')}
                alt={profileData.name || ''}
                className={`profile-card__avatar ${avatarLoaded ? 'profile-card__avatar--loaded' : ''}`}
                referrerPolicy="no-referrer"
                onLoad={() => setAvatarLoaded(true)}
                onError={(e) => {
                  const fb = buildFallbackAvatar(profileData.name || 'Gary Joe');
                  if (!String(e.target.src || '').startsWith('data:image/svg')) {
                    e.target.src = fb;
                  } else {
                    setAvatarLoaded(true);
                  }
                }}
              />
            )}
            {profileData.verified && !loading && (
              <div className="profile-card__verified">
                <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
                  <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            )}
          </div>
          {loading ? (
            <span className="profile-card__btn-skeleton" aria-hidden="true" />
          ) : (
            <button className="profile-card__follow-btn" onClick={handleFollowClick}>
              <svg viewBox="0 0 24 24" fill="none" width="18" height="18">
                <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {profileData.followButtonText || 'Follow'}
            </button>
          )}
        </div>

        {/* Info Section */}
        <div className="profile-card__info">
          {loading ? (
            <>
              <div className="profile-card__name-skeleton" aria-hidden="true" />
              <div className="profile-card__title-skeleton" aria-hidden="true" />
              <div className="profile-card__stats">
                <div className="profile-card__stat">
                  <span className="profile-card__stat-value-skeleton" aria-hidden="true" />
                  <span className="profile-card__stat-label">Years Experience</span>
                </div>
                <div className="profile-card__stat-divider"></div>
                <div className="profile-card__stat">
                  <span className="profile-card__stat-value-skeleton" aria-hidden="true" />
                  <span className="profile-card__stat-label">Total Trades</span>
                </div>
                <div className="profile-card__stat-divider"></div>
                <div className="profile-card__stat">
                  <span className="profile-card__stat-value-skeleton" aria-hidden="true" />
                  <span className="profile-card__stat-label">Win Rate</span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="profile-card__desk-row">
                <span className="profile-card__desk-tag">Research Desk</span>
                {profileData.verified && (
                  <span className="profile-card__verified-tag">Verified Analyst</span>
                )}
              </div>
              <h1 className="profile-card__name">{profileData.name || profileData.nameCN}</h1>
              <p className="profile-card__title">{profileData.title || profileData.titleCN}</p>
              {profileData.description ? (
                <p className="profile-card__bio">{profileData.description}</p>
              ) : null}

              {/* Stats */}
              <div className="profile-card__stats">
                <div className="profile-card__stat">
                  <span className="profile-card__stat-value">{profileData.stats?.experience || 0}</span>
                  <span className="profile-card__stat-label">Years Experience</span>
                </div>
                <div className="profile-card__stat-divider"></div>
                <div className="profile-card__stat">
                  <span className="profile-card__stat-value">{profileData.stats?.trades || 0}</span>
                  <span className="profile-card__stat-label">Total Trades</span>
                </div>
                <div className="profile-card__stat-divider"></div>
                <div className="profile-card__stat">
                  <span className="profile-card__stat-value profile-card__stat-value--success">{profileData.stats?.winRate || 0}%</span>
                  <span className="profile-card__stat-label">Win Rate</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileCard;
